import type { Id, IsoDate, IsoDateTime, Json } from "../primitives";

/** FR-TSK-4. Custom per-project statuses are deferred, so this is closed. */
export type TaskStatus =
  "todo" | "in_progress" | "blocked" | "done" | "cancelled";

export type TaskPriority = "low" | "medium" | "high" | "urgent";

/**
 * A container for tasks.
 *
 * `key` is short and uppercase because it prefixes every task reference the
 * team says out loud — `OPS-142`, not a UUID.
 */
export interface Project {
  id: Id;
  name: string;
  /** `"OPS"`. Uppercase, unique, and not editable once tasks exist. */
  key: string;
  description: string | null;
  ownerUserId: Id;
  isArchived: boolean;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/**
 * What a task can be attached to (FR-TSK-10).
 *
 * A closed union: each member has a detail screen that has to render the
 * reverse direction — an order showing its tasks, a contact showing theirs
 * (FR-TSK-11) — and an unknown member would mean a link pointing nowhere.
 */
export type TaskLinkTarget =
  "order" | "contact" | "transfer" | "invoice" | "product";

/** A task attached to the thing the work is about. */
export interface TaskLink {
  id: Id;
  taskId: Id;
  targetType: TaskLinkTarget;
  targetId: Id;
  createdByUserId: Id;
  createdAt: IsoDateTime;
}

/**
 * A unit of work.
 *
 * `parentTaskId` allows exactly one level of nesting (FR-TSK-5). Not because
 * a tree is hard, but because deeper hierarchies are not maintained in
 * practice — they get created once and then rot.
 */
export interface Task {
  id: Id;
  projectId: Id;
  /** `"OPS-142"`. Project key plus a per-project sequence. Stable forever. */
  reference: string;
  /** Null for a top-level task; set for a subtask. Never two levels deep. */
  parentTaskId: Id | null;

  title: string;
  /** Markdown, stored as written. */
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeUserId: Id | null;
  reporterUserId: Id;
  dueDate: IsoDate | null;
  tags: string[];
  watcherUserIds: Id[];

  /**
   * Soft delete (FR-TSK-18). The activity timeline is retained, which is the
   * point — a deleted task that took its own history with it cannot answer
   * why it was deleted.
   */
  deletedAt: IsoDateTime | null;

  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  completedAt: IsoDateTime | null;
}

/**
 * A Markdown comment on a task.
 *
 * `editedAt` is separate from `updatedAt` and exists to be shown: a comment
 * that changed after people read it should say so (FR-TSK-8).
 */
export interface TaskComment {
  id: Id;
  taskId: Id;
  authorUserId: Id;
  body: string;
  createdAt: IsoDateTime;
  editedAt: IsoDateTime | null;
  deletedAt: IsoDateTime | null;
}

/**
 * Per file on a task. The same cap as a Docs attachment; the screen says so
 * before a refused upload and the server enforces it (#625).
 */
export const MAX_TASK_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * A file on a task, stored in R2 (FR-TSK-13).
 *
 * No storage key: the bucket is private and the key never leaves the API. A
 * file is fetched from `TasksRepository.attachmentUrl`, an app route that
 * checks the permission on every request (#625).
 */
export interface TaskAttachment {
  id: Id;
  taskId: Id;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: Id;
  uploadedAt: IsoDateTime;
}

/** What happened to a task. */
export type TaskEventType =
  | "created"
  | "status_changed"
  | "assigned"
  | "unassigned"
  | "priority_changed"
  | "due_date_changed"
  | "field_edited"
  | "commented"
  | "comment_edited"
  | "linked"
  | "unlinked"
  | "attachment_added"
  | "attachment_removed"
  | "subtask_added"
  | "watcher_added"
  | "watcher_removed"
  | "deleted"
  | "restored";

/**
 * One row per change, append-only (FR-TSK-9).
 *
 * Same shape and same reasoning as `OrderEvent`: the timeline is built by
 * diffing before overwriting, and a replaced field with no event row tells
 * you the current value and nothing about when or from what.
 */
export interface TaskEvent {
  id: Id;
  taskId: Id;
  occurredAt: IsoDateTime;
  actorUserId: Id;
  eventType: TaskEventType;
  field: string | null;
  before: Json | null;
  after: Json | null;
}

/** A task with everything its detail view renders. */
export interface TaskWithDetails extends Task {
  subtasks: Task[];
  comments: TaskComment[];
  links: TaskLink[];
  attachments: TaskAttachment[];
  events: TaskEvent[];
}
